import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  deleteField,
  where,
  writeBatch,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firebase } from "../services/firebase-config";
import { nationalPhoneForStorage, normalizePhoneFields } from "./phoneValidation";
import { BaseUrl, COLLECTIONS, ROLES } from "../services/config";
import axiosInstance from "../services/axios";
import { getLoginToken } from "../services/storage";
import { contactHasActiveInvite } from "./clientInvite";

const defaultHeaders = {
  "Content-Type": "application/json",
  withCredentials: true,
};

export function isClientPortalRole(roleValue) {
  const roles = Array.isArray(roleValue)
    ? roleValue
    : roleValue
      ? [roleValue]
      : [];
  return roles.some(
    (r) => String(r || "").toLowerCase() === String(ROLES.CLIENT).toLowerCase()
  );
}

export function normalizePortalClientId(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "string") return raw.trim() || null;
  if (typeof raw === "object" && raw.id != null) return String(raw.id);
  return null;
}

/** Portal users: prefer linked profile client id over stale localStorage. */
export function resolvePortalClientId(auth) {
  const email = (auth?.userProfile?.email || "").trim().toLowerCase();
  const list = auth?.clients;
  const findByEmail = () => {
    if (!email || !Array.isArray(list)) return null;
    const found = list.find((cl) =>
      (cl?.contact || []).some(
        (ct) => (ct?.email || "").trim().toLowerCase() === email
      )
    );
    return found?.id != null ? String(found.id) : null;
  };

  if (isClientPortalRole(auth?.userProfile?.role)) {
    const fromProfile = normalizePortalClientId(auth?.userProfile?.client);
    if (fromProfile) return fromProfile;

    // For portal users, prefer contact-linked client over localStorage.
    // This prevents stale clientId from a previous session hiding history.
    const fromContacts = findByEmail();
    if (fromContacts) return fromContacts;
  }

  if (typeof window !== "undefined") {
    const ls = localStorage.getItem("clientId");
    if (ls && String(ls).trim()) return String(ls).trim();
  }

  const fromProfile = normalizePortalClientId(auth?.userProfile?.client);
  if (fromProfile) return fromProfile;

  return findByEmail();
}

/**
 * Keep clients.contact invite/login flags aligned with portal user access.
 */
export async function syncClientContactPortalFlags(email, clientId, flags = {}) {
  const normalizedEmail = String(email || "").trim();
  if (!normalizedEmail || !clientId) return { status: true, updated: false };

  const clientRef = doc(firebase, COLLECTIONS.CLIENT, clientId);
  const clientSnap = await getDoc(clientRef);
  if (!clientSnap.exists()) return { status: true, updated: false };

  const contacts = clientSnap.data()?.contact || [];
  let changed = false;
  const updatedContacts = contacts.map((c) => {
    if (c?.email !== normalizedEmail) return c;
    changed = true;
    return {
      ...c,
      ...(flags.invite !== undefined ? { invite: flags.invite } : {}),
      ...(flags.login !== undefined ? { login: flags.login } : {}),
    };
  });

  if (!changed) return { status: true, updated: false };

  await updateDoc(clientRef, {
    contact: updatedContacts,
    updatedAt: Date.now(),
  });
  return { status: true, updated: true };
}

/**
 * Portal revoke path: users table + Firebase Auth (removeUser) + client contact flags.
 */
export async function deactivatePortalUserAccess(userDocSnap, currentUserId) {
  if (!userDocSnap?.id) {
    return { status: false, message: "No active user found" };
  }

  const userId = userDocSnap.id;
  const userData = userDocSnap.data() || {};
  const firstName = userData?.firstName;
  const email = userData?.email || "";
  const clientId = userData?.client || null;

  await updateDoc(doc(firebase, COLLECTIONS.USERS, userId), {
    deleted: true,
    status: false,
    updatedBy: currentUserId,
    deletedBy: currentUserId,
    deletedAt: serverTimestamp(),
    updatedAt: Date.now(),
  });

  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  Promise.resolve()
    .then(async () => {
      await axiosInstance.post(
        `${BaseUrl}removeUser`,
        { id: userId, firstName, email },
        { headers: { ...defaultHeaders, ...authHeaders } }
      );
    })
    .catch((err) => console.error("Portal removeUser failed:", err));

  if (clientId && email) {
    await syncClientContactPortalFlags(email, clientId, {
      invite: false,
      login: false,
    });
  }

  return { status: true, userId, email, companyId: userData?.companyId || "", clientId };
}

/**
 * Restore portal user row access (auth account may still require client re-invite).
 */
export async function reactivatePortalUserAccess(userId, userData = {}, currentUserId) {
  if (!userId) return { status: false, message: "User id required" };

  await updateDoc(doc(firebase, COLLECTIONS.USERS, userId), {
    status: true,
    deleted: deleteField(),
    deletedAt: deleteField(),
    deletedBy: deleteField(),
    updatedBy: currentUserId,
    updatedAt: Date.now(),
  });

  const email = userData?.email;
  const clientId = userData?.client;
  if (email && clientId) {
    await syncClientContactPortalFlags(email, clientId, {
      invite: true,
      login: false,
    });
  }

  return { status: true };
}

export async function findClientPortalUserDoc(email, clientId = null) {
  const normalizedEmail = String(email || "").trim();
  if (!normalizedEmail) return null;

  const constraints = [
    where("email", "==", normalizedEmail),
    where("role", "==", ROLES.CLIENT),
  ];
  if (clientId) {
    constraints.push(where("client", "==", clientId));
  }

  const snapshot = await getDocs(
    query(collection(firebase, COLLECTIONS.USERS), ...constraints)
  );
  return snapshot?.docs?.find((d) => d.data()?.deleted !== true) || null;
}

export function getPortalSyncActorId() {
  const currentUser = getAuth().currentUser;
  return currentUser ? currentUser.uid : null;
}

export const PORTAL_EMAIL_CHANGE_BLOCKED_MESSAGE =
  "This email address is currently associated with an active Web Portal login invitation. To update the email, please revoke the existing invitation from the client record first, then proceed with the update.";

export async function getClientContactByEmail(clientId, email) {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (!clientId || !normalizedEmail) return null;

  const clientSnap = await getDoc(doc(firebase, COLLECTIONS.CLIENT, clientId));
  if (!clientSnap.exists()) return null;

  const contact = (clientSnap.data()?.contact || []).find(
    (c) => String(c?.email || "").trim().toLowerCase() === normalizedEmail
  );
  return contact || null;
}

export function portalContactHasActiveInvite(contact) {
  return contactHasActiveInvite(contact);
}

/**
 * Build a client doc patch when a portal user (role Client) is edited from users admin.
 * - Name: updates clientName only (not contact.name).
 * - Phone: updates matching contact.mobileNumber only.
 */
export function buildClientPatchForPortalUserUpdate(
  clientData,
  contactEmail,
  { firstName, lastName, phoneNumber, nameChanged = false, phoneChanged = false } = {}
) {
  const normalizedEmail = String(contactEmail || "").trim().toLowerCase();
  if (!normalizedEmail || !clientData) return null;

  const displayName = [firstName, lastName]
    .map((p) => String(p || "").trim())
    .filter(Boolean)
    .join(" ")
    .trim();

  const patch = {};
  let changed = false;

  if (
    nameChanged &&
    displayName &&
    displayName !== String(clientData?.clientName || "").trim()
  ) {
    patch.clientName = displayName;
    changed = true;
  }

  if (phoneChanged && phoneNumber !== undefined) {
    const contacts = clientData?.contact || [];
    let contactChanged = false;
    const updatedContacts = contacts.map((c) => {
      if (String(c?.email || "").trim().toLowerCase() !== normalizedEmail) return c;
      if (
        String(c?.mobileNumber || "").trim() === String(phoneNumber || "").trim()
      ) {
        return c;
      }
      contactChanged = true;
      return { ...c, mobileNumber: phoneNumber };
    });

    if (contactChanged) {
      patch.contact = updatedContacts;
      changed = true;
    }
  }

  if (!changed) return null;

  return {
    ...patch,
    updatedAt: Date.now(),
    updatedBy: getPortalSyncActorId(),
  };
}

/**
 * Step 2 / client save: invited contacts only — sync contact phone to users.phoneNumber.
 * Does not sync contact name to users.
 */
export async function resolveInvitedContactPhoneUserUpdates(
  clientId,
  previousContacts = [],
  nextContacts = [],
  companyCountry
) {
  if (!clientId || !Array.isArray(nextContacts) || !nextContacts.length) return [];

  const prevByEmail = new Map(
    (previousContacts || []).map((c) => [
      String(c?.email || "").trim().toLowerCase(),
      c,
    ])
  );

  const updates = [];

  for (const nextContact of nextContacts) {
    if (!contactHasActiveInvite(nextContact)) continue;

    const emailKey = String(nextContact?.email || "").trim().toLowerCase();
    if (!emailKey) continue;

    const prevContact = prevByEmail.get(emailKey);
    const resolvedCountry =
      nextContact?.country || companyCountry || undefined;
    const phoneFields = normalizePhoneFields(
      {
        phoneNumber: nextContact?.mobileNumber,
        country: resolvedCountry,
        phoneCountryCode: nextContact?.phoneCountryCode,
      },
      companyCountry
    );
    const oldPhone = nationalPhoneForStorage(
      prevContact?.mobileNumber,
      resolvedCountry
    );
    const newPhone = phoneFields.phoneNumber;
    if (!newPhone || oldPhone === newPhone) continue;

    const userDoc = await findClientPortalUserDoc(nextContact.email, clientId);
    if (!userDoc?.id) continue;

    updates.push({
      userId: userDoc.id,
      phoneNumber: newPhone,
      country: phoneFields.country,
      phoneCountryCode: phoneFields.phoneCountryCode,
    });
  }

  return updates;
}

/**
 * Atomically update users row and linked client record for portal (Client) users.
 */
export async function commitPortalUserAndClientUpdate({
  userDocRef,
  userUpdateData,
  clientId,
  contactEmail,
  clientPatch,
}) {
  const batch = writeBatch(firebase);
  batch.set(userDocRef, userUpdateData, { merge: true });
  if (clientId && clientPatch && Object.keys(clientPatch).length > 0) {
    batch.update(doc(firebase, COLLECTIONS.CLIENT, clientId), clientPatch);
  }
  await batch.commit();
}
